import Toastify from "toastify-js";
import "toastify-js/src/toastify.css";
import { announce } from "../../components/Layout/LiveAnnouncer";

const showToast = (text: string, type: "success" | "error") => {
  Toastify({
    text,
    duration: type === "success" ? 4000 : 8000,
    close: false,
    gravity: "top", // top or bottom
    position: "right", // left, center or right
    stopOnFocus: true,
    style: {
      background:
        type === "success"
          ? "linear-gradient(135deg, #4CAF50, #2E7D32)"
          : "linear-gradient(135deg, #F44336, #B71C1C)",
      borderRadius: "12px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
      padding: "12px 18px",
      fontSize: "14px",
    },
  }).showToast();
};

// toastify-js repositions all toasts on every showToast(), so they stack
// vertically — but a synchronous burst of notify() calls runs every slide-in
// transition at once, which can briefly overlap. Sequence appearances at least
// 200ms apart so each entrance animation is visually distinct.
const TOAST_MIN_GAP_MS = 200;
let toastChain: Promise<void> = Promise.resolve();

export const notify = (text: string, type: "success" | "error" = "success") => {
  // Announce immediately so screen readers aren't delayed by the queue.
  announce(text);
  toastChain = toastChain.then(() => {
    showToast(text, type);
    return new Promise<void>((resolve) =>
      setTimeout(resolve, TOAST_MIN_GAP_MS)
    );
  });
};

export class HTTPError extends Error {
  status: number;
  /** The parsed response body, for callers that act on a structured error. */
  body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "HTTPError"; // Helpful for distinguishing error types
    this.status = status;
    this.body = body;

    // Fix the prototype chain (important for instanceof checks in some environments)
    Object.setPrototypeOf(this, HTTPError.prototype);
  }
}

/**
 * True on iPhone, iPad, or iPod — including iPadOS in desktop mode, which
 * reports MacIntel + touch. We only care about iOS here because Patreon's
 * iOS app routes subscriptions through Apple IAP (~30% surcharge); Android's
 * Patreon app does not have the same fee model.
 */
export const isIOS = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return true;
  // iPadOS 13+ reports as MacIntel but exposes touch.
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
};

/**
 * True when the page is being rendered inside Patreon's in-app browser/WebView.
 * Their UA string includes "Patreon" in that context. If we detect this, the
 * popup-to-checkout flow would stay inside the app's WebView and still be
 * subject to Apple IAP — so we steer the user out to a real browser.
 */
export const isPatreonInAppBrowser = (): boolean => {
  if (typeof navigator === "undefined") return false;
  return /Patreon/i.test(navigator.userAgent);
};

export const handleOAuthLogin = () => {
  let loginUrl = `${import.meta.env.VITE_API_PATH}/discord/auth/login`;
  const currentPath = window.location.pathname;

  if (currentPath !== "/" && currentPath !== "") {
    const encodedPath = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    loginUrl += `?state=${window.location.origin + encodedPath}`;
  }

  // Check if inside an iframe
  if (window.self !== window.top) {
    // Open in a new tab if inside iframe
    window.open(loginUrl, "_blank", "noopener,noreferrer");
  } else {
    // Normal redirect
    window.location.href = loginUrl;
  }
};

// --- Silent token refresh infrastructure ---
let refreshPromise: Promise<boolean> | null = null;

async function attemptTokenRefresh(): Promise<boolean> {
  try {
    const res = await fetch(
      `${import.meta.env.VITE_API_PATH}/discord/auth/refresh`,
      { method: "POST", credentials: "include" }
    );
    return res.ok;
  } catch {
    return false;
  }
}

function refreshOnce(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = attemptTokenRefresh().finally(() => {
    refreshPromise = null;
  });
  return refreshPromise;
}

/** Run a request with the same one-time silent 401 recovery as handleFetch. */
export async function fetchWithAuthRefresh(
  fetchFn: () => Promise<Response>
): Promise<Response> {
  let response = await fetchFn();
  if (response.status !== 401) return response;

  if (await refreshOnce()) response = await fetchFn();
  return response;
}

export async function handleFetch<T>(
  fetchFn: () => Promise<Response>,
  onSuccess: (data: T) => void,
  onUnauthorized?: () => void,
  onError?: (err: unknown) => void
): Promise<void> {
  try {
    const res = await fetchWithAuthRefresh(fetchFn);
    if (res.status === 401) {
      onUnauthorized?.();
      return;
    }

    const clonedRes = res.clone();

    let data: T | null = null;

    if (res.status !== 204) {
      data = (await clonedRes.json()) as T;
    }

    if (!res.ok) {
      interface ErrorShape {
        errors?:
          | string
          | {
              errors?: {
                message?: unknown;
                error?: unknown;
                issues?: unknown[];
              };
              message?: unknown;
              error?: unknown;
              issues?: unknown[];
            };
        message?: unknown;
      }

      const errorData = data as ErrorShape;

      let rawMessage: unknown;
      let details: string[] = [];

      if (typeof errorData?.errors === "string") {
        rawMessage = errorData.errors;
      } else if (
        typeof errorData?.errors === "object" &&
        errorData.errors !== null
      ) {
        rawMessage =
          errorData.errors.errors?.message ??
          errorData.errors.errors?.error ??
          errorData.errors.message ??
          errorData.errors.error;

        // Collect issue messages if present
        const issues =
          errorData.errors.issues ?? errorData.errors.errors?.issues;
        if (Array.isArray(issues)) {
          details = issues
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .map((issue: any) => issue?.message)
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            .filter((msg: any) => typeof msg === "string");
        }
      } else {
        rawMessage = errorData?.message;
      }

      let errorMessage =
        typeof rawMessage === "string"
          ? rawMessage
          : typeof rawMessage === "object"
          ? JSON.stringify(rawMessage)
          : String(rawMessage ?? "Request failed");

      if (details.length > 0) {
        errorMessage += "\n" + details.join("\n");
      }

      throw new HTTPError(errorMessage, res.status, data);
    }

    onSuccess(data as T);
  } catch (err: unknown) {
    if (err instanceof HTTPError && err.status === 401) {
      onUnauthorized?.();
      return;
    }

    let message = "Unexpected error occurred";

    if (err instanceof Error) {
      message =
        typeof err.message === "string"
          ? err.message
          : typeof err.message === "object"
          ? JSON.stringify(err.message)
          : String(err.message);
    }

    notify(message, "error");
    onError?.(err);
  }
}
